import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createGlucometerOnboardingTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is ${userFullname} with Tellihealth.
          <br />
          I'm calling to help you get started with your Remote Patient
          Monitoring program.
          <br />
          For quality and compliance purposes, this call may be recorded.
          <br />
          To protect your privacy, can you please confirm your full name and
          date of birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'onboardingPurposeReassurance',
      type: 'checkbox',
      title: <p>Onboarding Purpose & Reassurance</p>,
      description: (
        <ScriptWrapper>
          "The goal of today's call is to help you get your glucometer set up,
          review how to use the supplies, and take your first reading if we're
          able."
          <br />
          "There's no way to do this wrong — I'll guide you step by step, and if
          something doesn't work right away, that's completely okay."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Purpose explained',
          value: 'purposeExplained',
        },
        {
          key: makeid(10),
          label: 'Reassurance provided',
          value: 'reassuranceProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'earlyGoalSetting',
      type: 'checkbox',
      title: <p>Early Goal Setting (Clinical Best Practice)</p>,
      description: (
        <ScriptWrapper>
          "As part of your care, your clinic recommends checking your blood
          sugar as often as directed by your provider, commonly daily or as
          instructed."
          <br />
          "These readings help your care team understand your usual range and
          notice changes early."
          <br />
          "If you ever miss a check, that's okay — consistency over time is what
          matters most."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Blood sugar monitoring encouraged per provider guidance',
          value: 'bloodSugarMonitoringEncouragedPerProviderGuidance',
        },
        {
          key: makeid(10),
          label: 'Flexibility and consistency emphasized',
          value: 'flexibilityAndConsistencyEmphasized',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceSupplyReadiness',
      type: 'radio',
      title: <p>Device & Supply Readiness Check</p>,
      description: (
        <ScriptWrapper>
          "Do you have the glucometer box with you right now?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Supplies present during call',
          value: 'suppliesPresentDuringCall',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — let's go through what's in the box together."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Supplies not present during call',
          value: 'suppliesNotPresentDuringCall',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "That's okay — we can review how everything works today, and
                  you can complete your first check later."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'powerChargingReviewed',
      type: 'checkbox',
      title: <p>Power & Charging Setup</p>,
      description: (
        <ScriptWrapper>
          "Before we take a reading, let's make sure the glucometer has power."
          <br />
          "If it uses a rechargeable battery, please plug it in using the
          charging cable and wall adapter."
          <br />
          "Let me know once the screen turns on or shows it's charging."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Device powered on',
          value: 'devicePoweredOn',
        },
        {
          key: makeid(10),
          label: 'Charging reviewed',
          value: 'chargingReviewed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'qualityControlEducation',
      type: 'checkbox',
      title: <p>Quality Control (QC) Explanation & Importance</p>,
      description: (
        <ScriptWrapper>
          "I want to explain the test solution, which is also called quality
          control or QC solution."
          <br />
          "This solution is not for checking your blood sugar — it's used to
          make sure the meter and test strips are working properly."
          <br />
          "You won't be prompted by the device to do a QC check."
          <br />
          "Instead, we recommend using the control solution if a reading doesn't
          seem right, if the meter is dropped, or on a fairly regular basis to
          make sure everything is working as expected."
          <br />
          "QC checks help confirm that the device and strips are giving reliable
          results."
          <br />
          "We won't use the control solution for your blood sugar reading today,
          but it's important to keep it available."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'QC solution purpose explained',
          value: 'qcSolutionPurposeExplained',
        },
        {
          key: makeid(10),
          label: 'When to use QC explained',
          value: 'whenToUseQcExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'lancetFingerPrepReviewed',
      type: 'checkbox',
      title: <p>Finger Preparation & Lancet Setup</p>,
      description: (
        <ScriptWrapper>
          "Now let's review how to prepare for a blood sugar check."
          <br />
          "Wash and dry your hands before testing."
          <br />
          "Insert a lancet into the lancet pen."
          <br />
          "Adjust the depth if needed — many people start with a lower setting."
          <br />
          "Let me know when that's ready."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Lancet pen setup reviewed',
          value: 'lancetPenSetupReviewed',
        },
        {
          key: makeid(10),
          label: 'Hand hygiene discussed',
          value: 'handHygieneDiscussed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'testStripReadingPreparation',
      type: 'checkbox',
      title: <p>Test Strip & Reading Preparation</p>,
      description: (
        <ScriptWrapper>
          "Insert a test strip into the glucometer."
          <br />
          "The meter should turn on or indicate it's ready for a sample."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Test strip inserted correctly',
          value: 'testStripInsertedCorrectly',
        },
        {
          key: makeid(10),
          label: 'Meter ready for sample',
          value: 'meterReadyForSample',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'firstBloodSugarReading',
      type: 'radio',
      title: <p>First Blood Sugar Reading</p>,
      description: (
        <ScriptWrapper>
          "When you're ready, use the lancet pen to prick the side of your
          fingertip."
          <br />
          "Gently squeeze until you see a small drop of blood."
          <br />
          "Touch the edge of the test strip to the drop — it will draw the blood
          in."
          <br />
          (Allow time.) "You should see a number on the screen once the reading
          is complete."
          <br />
          "Please tell me the number you see."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Reading completed successfully',
          value: 'readingCompletedSuccessfully',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Perfect — you did it. That reading went through."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Reading attempted but unsuccessful',
          value: 'readingAttemptedButUnsuccessful',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "That's okay — sometimes it takes a little practice. We can
                  try again or follow up later."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Reading deferred',
          value: 'readingDeferred',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'programValueReinforced',
      type: 'checkbox',
      title: <p>Program Value & Engagement Reinforcement</p>,
      description: (
        <ScriptWrapper>
          "Your blood sugar readings are securely shared with your care team,
          who review trends over time."
          <br />
          "Even readings that feel normal are important — they help your
          provider understand how your treatment plan is working."
          <br />
          "If readings are missing for an extended period, you may receive
          reminder texts or a call from us — just to check in and see if you
          need support."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Trend monitoring explained',
          value: 'trendMonitoringExplained',
        },
        {
          key: makeid(10),
          label: 'Engagement encouraged',
          value: 'engagementEncouraged',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we have on file for you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Which plan is listed?"
                  <br />
                  "Thank you — I'll confirm what's documented in your chart."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              type: 'checkbox',
              name: 'insuranceTypeOnFile',
              options: [
                {
                  key: makeid(10),
                  label: 'Insurance type on file confirmed',
                  value: 'insuranceTypeOnFileConfirmed',
                },
              ],
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "Before we continue, I want to confirm the best cell phone number to
          reach you. Is [number on file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone number confirmed',
          value: 'cellPhoneNumberConfirmed',
        },
        {
          key: makeid(10),
          label: 'Cell phone number updated',
          value: 'cellPhoneNumberUpdated',
        },
        {
          key: makeid(10),
          label: 'Patient reports no cell phone',
          value: 'patientReportsNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          "If you have a cell phone, we also offer text messages for reminders
          and updates related to your RPM care."
          <br />
          "Would you like to receive text messages from us?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered and accepted → Enrolled',
          value: 'offeredAndAcceptedEnrolled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — I'll get you enrolled. You can opt out at any time."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Offered and declined',
          value: 'offeredAndDeclined',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "No problem — that's completely optional."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Not eligible — no cell phone',
          value: 'notEligibleNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Scheduling</p>,
      description: (
        <ScriptWrapper>
          "As part of the program, a monthly phone call with our team is
          required."
          <br />
          "These calls help us check in, answer questions, and make sure
          everything continues to work smoothly."
          <br />
          "When your readings stay within your doctor's recommended range,
          they're reviewed as trends over time."
          <br />
          "If a reading falls outside of that range, your clinic may reach out —
          often with a call from a nurse — to check in and decide if follow-up
          is needed."
          <br />
          "Would you like to schedule your first monthly check-in now, or should
          we reach out closer to when it's due?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monthly phone call requirement reviewed',
          value: 'monthlyPhoneCallRequirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Program monitoring process explained',
          value: 'programMonitoringProcessExplained',
        },
        {
          key: makeid(10),
          label: 'Nurse or clinic outreach for out-of-range readings explained',
          value: 'nurseOrClinicOutreachForOutOfRangeReadingsExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TGlucometerOnboardingTemplateValues = ExtractStepValues<
  ReturnType<typeof createGlucometerOnboardingTemplate>
>;

export const createGlucometerOnboardingBlurb = (
  data: TGlucometerOnboardingTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    onboardingPurposeReassurance,
    earlyGoalSetting,
    deviceSupplyReadiness,
    powerChargingReviewed,
    qualityControlEducation,
    lancetFingerPrepReviewed,
    testStripReadingPreparation,
    firstBloodSugarReading,
    programValueReinforced,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;
  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    onboardingPurposeReassurance?.length &&
    !hasField(ctx, 'onboardingPurposeReassurance')
  ) {
    addField(ctx, 'onboardingPurposeReassurance');
    const map = {
      purposeExplained: 'Onboarding call purpose explained.',
      reassuranceProvided:
        'Patient reassured regarding glucometer setup and available support.',
    };
    txt.push(...mapValuesToText(onboardingPurposeReassurance, map));
  }

  if (earlyGoalSetting?.length && !hasField(ctx, 'earlyGoalSetting')) {
    addField(ctx, 'earlyGoalSetting');
    const map = {
      bloodSugarMonitoringEncouragedPerProviderGuidance:
        'Clinical best practice for blood glucose monitoring discussed per provider guidance.',
      flexibilityAndConsistencyEmphasized:
        'Importance of consistency over time emphasized.',
    };
    txt.push(...mapValuesToText(earlyGoalSetting, map));
  }

  if (deviceSupplyReadiness && !hasField(ctx, 'deviceSupplyReadiness')) {
    addField(ctx, 'deviceSupplyReadiness');
    const map = {
      suppliesPresentDuringCall: 'present',
      suppliesNotPresentDuringCall: 'not present',
    };

    txt.push(
      `Glucometer and supply availability reviewed. Supplies ${map[deviceSupplyReadiness]}.`,
    );

    if (deviceSupplyReadiness === 'suppliesNotPresentDuringCall') {
      txt.push(`Education provided and first reading deferred.`);
    }
  }

  if (
    powerChargingReviewed?.length &&
    !hasField(ctx, 'powerChargingReviewed')
  ) {
    addField(ctx, 'powerChargingReviewed');
    const map = {
      devicePoweredOn: 'device powered on',
      chargingReviewed: 'charging setup reviewed',
    };
    const items = mapValuesToText(powerChargingReviewed, map);

    if (items.length) {
      txt.push(
        `Glucometer power and charging setup reviewed and confirmed: ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    qualityControlEducation?.length &&
    !hasField(ctx, 'qualityControlEducation')
  ) {
    addField(ctx, 'qualityControlEducation');
    const map = {
      qcSolutionPurposeExplained: 'QC solution purpose explained',
      whenToUseQcExplained: 'when to use QC explained',
    };
    const items = mapValuesToText(qualityControlEducation, map);
    if (items.length) {
      txt.push(
        `Quality control (QC) solution education provided, including ${formatList(
          items,
        )}.`,
      );
      txt.push(
        'Patient informed that QC checks are not prompted by the device and are recommended if a reading does not seem right, after device issues, or periodically to confirm device and test strip accuracy.',
      );
    }
  }

  if (
    lancetFingerPrepReviewed?.length &&
    !hasField(ctx, 'lancetFingerPrepReviewed')
  ) {
    addField(ctx, 'lancetFingerPrepReviewed');
    const map = {
      lancetPenSetupReviewed: 'lancet pen setup reviewed',
      handHygieneDiscussed: 'hand hygiene discussed',
    };
    const items = mapValuesToText(lancetFingerPrepReviewed, map);

    if (items.length) {
      txt.push(
        `Finger preparation and lancet pen setup reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    testStripReadingPreparation?.length &&
    !hasField(ctx, 'testStripReadingPreparation')
  ) {
    addField(ctx, 'testStripReadingPreparation');
    const map = {
      testStripInsertedCorrectly: 'test strip inserted correctly',
      meterReadyForSample: 'meter ready for sample',
    };
    const items = mapValuesToText(testStripReadingPreparation, map);

    if (items.length) {
      txt.push(
        `Test strip insertion and meter readiness reviewed, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (firstBloodSugarReading && !hasField(ctx, 'firstBloodSugarReading')) {
    addField(ctx, 'firstBloodSugarReading');
    const map = {
      readingCompletedSuccessfully: 'completed successfully',
      readingAttemptedButUnsuccessful: 'attempted but unsuccessful',
      readingDeferred: 'deferred',
    };
    txt.push(`First blood glucose reading ${map[firstBloodSugarReading]}.`);
  }

  if (
    programValueReinforced?.length &&
    !hasField(ctx, 'programValueReinforced')
  ) {
    addField(ctx, 'programValueReinforced');
    const map = {
      trendMonitoringExplained: 'importance of blood glucose trends explained',
      engagementEncouraged: 'patient engagement encouraged',
    };
    const items = mapValuesToText(programValueReinforced, map);

    if (items.length) {
      txt.push(
        `Program value reviewed, including ${formatList(
          items,
        )} for ongoing care.`,
      );
    }
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare Advantage (plan selected)',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} and documented in patient chart.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneNumberConfirmed: 'confirmed',
      cellPhoneNumberUpdated: 'updated',
      patientReportsNoCellPhone: 'patient reports no cell phone',
    };
    txt.push(
      `Cell phone contact verified with patient ${map[cellPhoneVerification]}.`,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAndAcceptedEnrolled: 'offered and accepted',
      offeredAndDeclined: 'offered and declined',
      notEligibleNoCellPhone: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      monthlyPhoneCallRequirementReviewed: 'monthly phone call requirement',
      programMonitoringProcessExplained: 'program monitoring process explained',
      nurseOrClinicOutreachForOutOfRangeReadingsExplained:
        'that out-of-range readings may result in nurse or clinic outreach',
    };

    const items = mapValuesToText(monthlyInteractionRequirement, map);

    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join();
};
